// High-level "what is happening right now" box shown next to the live matrix, so
// it is obvious the run is progressing and not stuck.
import { useEffect, useState } from 'react';

export interface ActivityState {
  phase: 'starting' | 'measuring' | 'aggregating' | 'finished';
  model?: string;
  input?: number;
  output?: number;
  repetition?: number;
  repetitions?: number;
  promptSystem?: string;
  promptUser?: string;
  responseText?: string;
  done: number;
  total: number;
  since: number; // ms timestamp when the current step began
  lastEventAt: number; // ms timestamp of the last stream event
}

function tokenLabel(n?: number): string {
  if (n == null) return '?';
  return n >= 1000 && n % 1000 === 0 ? `${n / 1000}k` : String(n);
}

const PHASE_TEXT: Record<ActivityState['phase'], string> = {
  starting: 'Starting the model run…',
  measuring: 'Measuring a matrix cell',
  aggregating: 'Aggregating repetitions',
  finished: 'Model run finished',
};

export default function RunActivity({ activity }: { activity: ActivityState | null }) {
  const [, force] = useState(0);
  useEffect(() => {
    const iv = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(iv);
  }, []);

  if (!activity) {
    return (
      <aside className="run-activity">
        <div className="run-activity-head">Live activity</div>
        <p className="muted small">Waiting for the run to start…</p>
      </aside>
    );
  }

  const stepSecs = Math.max(0, Math.round((Date.now() - activity.since) / 1000));
  const agoSecs = Math.max(0, Math.round((Date.now() - activity.lastEventAt) / 1000));
  const stale = activity.phase !== 'finished' && agoSecs > 120;
  const pct =
    activity.total > 0 ? Math.round((activity.done / activity.total) * 100) : 0;

  return (
    <aside className={`run-activity${stale ? ' stale' : ''}`}>
      <div className="run-activity-head">
        <span className={`run-activity-dot ${activity.phase}`} />
        Live activity
      </div>

      <div className="run-activity-phase">{PHASE_TEXT[activity.phase]}</div>

      <dl className="run-activity-list">
        {activity.model ? (
          <>
            <dt>Model</dt>
            <dd title={activity.model}>{activity.model}</dd>
          </>
        ) : null}
        {activity.phase === 'measuring' ? (
          <>
            <dt>Cell</dt>
            <dd>
              input {tokenLabel(activity.input)} · output {tokenLabel(activity.output)}
            </dd>
            <dt>Repetition</dt>
            <dd>
              {activity.repetition ?? '?'} / {activity.repetitions ?? '?'}
            </dd>
          </>
        ) : null}
        <dt>This step</dt>
        <dd>{stepSecs}s</dd>
        <dt>Progress</dt>
        <dd>
          {activity.done} / {activity.total} cells ({pct}%)
        </dd>
        <dt>Last update</dt>
        <dd className={stale ? 'warn' : undefined}>{agoSecs}s ago</dd>
      </dl>

      {stale ? (
        <p className="run-activity-warn">
          No stream update for a while — a single request can take long, but check the pod
          logs if this keeps growing.
        </p>
      ) : null}

      {activity.promptUser ? (
        <details className="run-io">
          <summary>Sent to the model</summary>
          {activity.promptSystem ? (
            <pre className="run-io-block">
              <span className="run-io-role">system</span>
              {activity.promptSystem}
            </pre>
          ) : null}
          <pre className="run-io-block">
            <span className="run-io-role">user</span>
            {activity.promptUser}
          </pre>
        </details>
      ) : null}

      {activity.responseText ? (
        <details className="run-io" open>
          <summary>Model response</summary>
          <pre className="run-io-block">{activity.responseText}</pre>
        </details>
      ) : null}
    </aside>
  );
}
